<template>
  <div class="custom-fields-tab">
    <div class="header-section">
      <h3>ServiceM8 Custom Fields</h3>
      <button @click="showCreateModal = true" class="btn-create-field">
        Create Custom Field
      </button>
    </div>

    <div v-if="loading" class="loading">Loading custom fields...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else-if="customFields.length === 0" class="no-data">
      <p>No custom fields found. Click "Create Custom Field" to add one.</p>
    </div>
    <div v-else class="custom-fields-list">
      <table class="fields-table">
        <thead>
          <tr>
            <th>Display Name</th>
            <th>Field Name</th>
            <th>Type</th>
            <th>Object</th>
            <th>Field Type</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="field in customFields" :key="field.id || field.field_name">
            <td>{{ field.display_name }}</td>
            <td><code>{{ field.field_name }}</code></td>
            <td>{{ field.field_type }}</td>
            <td>{{ field.object_name }}</td>
            <td>
              <span v-if="field.field_type === 'SelectList'" class="select-list-info">
                {{ field.select_list_items?.length || 0 }} item(s)
              </span>
              <span v-else>—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create Custom Field Modal -->
    <CreateCustomFieldModal
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @created="handleFieldCreated"
    />
  </div>
</template>

<script>
import CreateCustomFieldModal from './CreateCustomFieldModal.vue'

export default {
  name: 'CustomFieldsTab',
  components: {
    CreateCustomFieldModal,
  },
  data() {
    return {
      customFields: [],
      loading: false,
      error: null,
      showCreateModal: false,
    }
  },
  mounted() {
    this.loadCustomFields()
  },
  methods: {
    async loadCustomFields() {
      this.loading = true
      this.error = null
      try {
        const ssoToken = await this.getSSOToken()
        const headers = { 'x-sso-token': ssoToken }
        
        const response = await fetch('/servicem8/custom-fields', { headers })
        if (response.ok) {
          const data = await response.json()
          this.customFields = data.fields || []
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
          this.error = errorData.error || 'Failed to load custom fields'
        }
      } catch (error) {
        console.error('Error loading custom fields:', error)
        this.error = error.message || 'Failed to load custom fields'
      } finally {
        this.loading = false
      }
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
        window.addEventListener("message", ({ data }) => {
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            resolve(data.payload);
          }
        });
      });
    },
    handleFieldCreated() {
      this.showCreateModal = false
      this.loadCustomFields()
    },
  },
}
</script>

<style scoped>
.custom-fields-tab {
  width: 100%;
  padding: 20px;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-section h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.btn-create-field {
  padding: 10px 20px;
  background: #24ACFE;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-create-field:hover {
  background: #1a8fd8;
}

.loading, .error, .no-data {
  padding: 40px;
  text-align: center;
  color: #666;
}

.error {
  color: #d32f2f;
}

.custom-fields-list {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.fields-table {
  width: 100%;
  border-collapse: collapse;
}

.fields-table thead {
  background: #f5f5f5;
}

.fields-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #666;
  border-bottom: 2px solid #e0e0e0;
}

.fields-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #333;
}

.fields-table tbody tr:hover {
  background: #fafafa;
}

.fields-table code {
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #d63384;
}

.select-list-info {
  color: #666;
  font-size: 13px;
}
</style>
